import { ColumnOptionsButton, SearchResultsCount } from "@greco/components";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { IconButton, TooltipHost } from "@fluentui/react";
import { ClearFilters } from "../../../components/filters/ClearFilters";
import { ClientsFilter } from "../../../components/filters/ClientsFilter";
import { Filters } from "../../../components/filters/Filters";
import { LocationsFilter } from "../../../components/filters/LocationsFilter";
import { ReportsFilter } from "../../../components/filters/ReportsFilter";
import { NavButtons } from "../../../components/navigation/NavButtons";
import { Flex, HSpace, NavigationBar, Right } from "../../../components/styled";
import { routes } from "../../../config/routes";
import {
  setClientsFilter,
  setLocationsFilter,
  setReportsFilter,
} from "../../../store/filters/filters";
import { useSelector } from "../../../store/hooks";
import { selectIsAnyFiltered } from "../../../store/recommendations-page/selectors";
import { AppDispatch } from "../../../store/store";
import { setManageLocationsDialog } from "../../../store/users";
import { setManageReportsDialog } from "../../../store/usersinsurer";
import { ColumnOptions } from "../ColumnOptions";
import { ExportMenu } from "./ExportMenu";
import { RefreshButton } from "./RefreshButton";
import { SearchRecommendations } from "./SearchRecommendations";

type Props = {
  refExcelExport: ExcelExport;
};

export const Navigation = ({ refExcelExport }: Props) => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const clientsFilter = useSelector((s) => s.filters.clientsFilter);
  const locationsFilter = useSelector((s) => s.filters.locationsFilter);
  const reportsFilter = useSelector((s) => s.filters.reportsFilter);
  const count = useSelector((s) => s.recommendations.count);
  const userRole = useSelector((s) => s.userRole.userRole);
  const { t } = useTranslation();
  const [isColumnOptionsOpen, setIsColumnOptionsOpen] = useState(false);
  const isAnyColumnFiltered = useSelector(selectIsAnyFiltered as any);

  const isAdmin = userRole === "admin";

  const canClear = [clientsFilter, locationsFilter, reportsFilter].some(
    (f) => f.length > 0,
  );

  const handleEditMode = () => {
    navigate(routes.recommendationsEditable.getPath());
  };

  return (
    <NavigationBar>
      <NavButtons />
      <SearchRecommendations />
      <HSpace />
      <RefreshButton />
      {isAdmin && (
        <TooltipHost content={t("ram.recommendationsEditable.editMode")}>
          <IconButton
            iconProps={{ iconName: "Edit" }}
            onClick={handleEditMode}
            ariaLabel={t("ram.recommendationsEditable.editMode")}
          />
        </TooltipHost>
      )}
      <Filters>
        <ClientsFilter />
        <LocationsFilter />
        <ReportsFilter />
        <ClearFilters
          isDisabled={!canClear}
          onClear={() => {
            dispatch(setClientsFilter([]));
            dispatch(setLocationsFilter([]));
            dispatch(setReportsFilter([]));
            dispatch(
              setManageLocationsDialog({
                user: null,
                isOpen: false,
              }),
            );
            dispatch(
              setManageReportsDialog({
                user: null,
                isOpen: false,
              }),
            );
          }}
        />
      </Filters>
      <ExportMenu refExcelExport={refExcelExport} />
      <Right>
        <Flex>
          <ColumnOptionsButton
            isActive={!!isAnyColumnFiltered}
            onClick={() => {
              setIsColumnOptionsOpen(true);
            }}
            t={t}
          />
          {isColumnOptionsOpen && (
            <ColumnOptions closePanel={() => setIsColumnOptionsOpen(false)} />
          )}
          <SearchResultsCount
            count={count || 0}
            label={t("greco.searchResults")}
          />
          <HSpace width={10} />
        </Flex>
      </Right>
    </NavigationBar>
  );
};
